import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnDestroy,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {SearchWidgetLabelsEnum} from '../../../enums/search-widget-labels.enum';
import {SearchWidgetFreeSearchFieldsEnum} from '../../../enums/search-widget-free-search-fields.enum';
import {SearchWidgetContentListItem} from '../../../../../types/SearchWidgetContentListItem';
import {SearchSuggestItem} from '../../../../../types/SearchSuggestItem';
import {SearchWidgetDataItem} from '../../../../../types/SearchWidgetDataItem';
import {SearchSuggestPayload} from '../../../../../types/SearchSuggestPayload';
import {SearchSuggestTypesEnum} from '../../../../../enums/SearchEnums';
import {ToolsService} from '../../../../../services/tools.service';
import {SearchWidgetManagerService} from '../../../../../services/search-widget/search-widget-manager.service';
import {Router} from '@angular/router';
import {SearchService} from '../../../../../services/search/search.service';
import {SearchReloadService} from '../../../../../services/search/search-reload.service';
import {Subscription} from 'rxjs';
import {EventCategoryEnum} from '../../../../../enums/GaTrackingEnums';
import {GtmEventEnum} from '../../../../../enums/GoogleEnums';
import {DLPageTypeEnum} from '../../../../../enums/DataLayerEnums';

@Component({
  selector: 'app-search-widget-v4-mobile-suggestions',
  templateUrl: './search-widget-v4-mobile-suggestions.component.html',
  styleUrls: ['./search-widget-v4-mobile-suggestions.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchWidgetV4MobileSuggestionsComponent implements OnDestroy {
  private static NumberOfSuggestionsToDisplay = 10;
  private static NumberOfPopularToDisplay = 6;
  public suggestTypes = input<string[]>([]);
  public mainCareProviderTypeId = input<number>();
  public inferredCareProviderTypeAreaId = input<number>();
  public inferredCategoryTypeId = input<number>();
  public pageType = input<DLPageTypeEnum>();
  public whichIsFocused = input<SearchWidgetFreeSearchFieldsEnum>();
  protected readonly SearchWidgetLabelsEnum = SearchWidgetLabelsEnum;
  protected readonly SearchWidgetFreeSearchFieldsEnum = SearchWidgetFreeSearchFieldsEnum;
  protected placePopularSuggestItems = signal<Array<SearchWidgetContentListItem>>(null);
  protected placeFilteredSuggestItems = signal<Array<SearchSuggestItem>>(null);
  protected freeSearchPopularSuggestItems = signal<Array<SearchWidgetDataItem>>(null);
  protected freeSearchFilteredSuggestItems = signal<Array<SearchSuggestItem>>(null);
  private isFreeSearchInInitialState = signal<boolean>(true);
  private subscriptions: Subscription[] = [];

  private searchService = inject(SearchService);
  protected state = this.searchService.searchWidgetState;
  private places = this.searchService.places;
  private widgetData = this.searchService.searchWidgetData;

  constructor(
    private tools: ToolsService,
    private searchWidgetManagerService: SearchWidgetManagerService,
    private router: Router,
    private reloadService: SearchReloadService,
  ) {
    effect(
      () => {
        if (this.places() && this.state()) {
          this.updatePlaceSuggestions(this.state().placeSearchValueLabel);
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (this.state() && this.widgetData() && this.whichIsFocused()) {
          this.updateSuggestions();
        }
      },
      {allowSignalWrites: true},
    );
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  protected onFreeSearchPopularOptionClick(option: SearchWidgetDataItem) {
    this.searchWidgetManagerService.registerGtmGa4Event(
      `${EventCategoryEnum.SEARCHNAV} - free search suggestion selected`,
      GtmEventEnum.CTA_CLICK,
      this.pageType(),
      this.mainCareProviderTypeId(),
      this.inferredCareProviderTypeAreaId(),
      this.inferredCategoryTypeId(),
    );

    this.reloadService.reload(option.routerLink, option.queryParams);
    this.closePanel();
    this.router.navigate(option.routerLink, {queryParams: option.queryParams});
  }

  protected searchByLocationClick(): void {
    this.searchWidgetManagerService.registerGtmGa4Event(
      `${EventCategoryEnum.SEARCHNAV} - search by location click`,
      GtmEventEnum.CTA_CLICK,
      this.pageType(),
      this.mainCareProviderTypeId(),
      this.inferredCareProviderTypeAreaId(),
      this.inferredCategoryTypeId(),
    );

    this.searchService.isLocationLoading.set(true);
    this.searchWidgetManagerService.getUserLocation(null, true, false, true);
  }

  protected onPlaceOptionClick(placeName: string) {
    this.searchWidgetManagerService.registerGtmGa4Event(
      `${EventCategoryEnum.SEARCHNAV} - place suggestion selected`,
      GtmEventEnum.CTA_CLICK,
      this.pageType(),
      this.mainCareProviderTypeId(),
      this.inferredCareProviderTypeAreaId(),
      this.inferredCategoryTypeId(),
    );
    this.searchWidgetManagerService.onPlacesSelection(placeName, this.state().autocompleteCurrentValue);
  }

  protected onFreeSearchOptionClick(selectedId, selectedTterm: string) {
    this.searchWidgetManagerService.registerGtmGa4Event(
      `${EventCategoryEnum.SEARCHNAV} - free search suggestion selected`,
      GtmEventEnum.CTA_CLICK,
      this.pageType(),
      this.mainCareProviderTypeId(),
      this.inferredCareProviderTypeAreaId(),
      this.inferredCategoryTypeId(),
    );

    const selectedObject: SearchSuggestItem = this.getFreeSearchSuggestItemBySuggestValueOrIndex(
      selectedTterm,
      selectedId,
    );
    this.searchWidgetManagerService.redirectToSearchResultsForSelectedSuggestItem(selectedObject);
  }

  private updateFreeSearchSuggestions(term: string): void {
    if (this.tools.isNil(term) || term.length === 0) {
      return this.updateFreeSearchSuggestionsWithPopularSearches();
    }
    return this.updateFreeSearchSuggestionsWithQuery(term);
  }

  private updateFreeSearchSuggestionsWithPopularSearches(): void {
    if (!this.tools.isNil(this.widgetData())) {
      this.freeSearchPopularSuggestItems.set(this.widgetData().popularCaregiverUnitTypes);
      this.freeSearchFilteredSuggestItems.set(null);
    }
  }

  private updateFreeSearchSuggestionsWithQuery(term: string): void {
    if (!this.tools.isNil(term) && term.length > 0 && !this.isFreeSearchInInitialState()) {
      const suggestPayload: SearchSuggestPayload = {
        term: encodeURIComponent(this.tools.clone(term).trim()),
        size: SearchWidgetV4MobileSuggestionsComponent.NumberOfSuggestionsToDisplay,
      };
      if (this.suggestTypes() && this.suggestTypes().length > 0) {
        suggestPayload.types = this.suggestTypes();
      }
      this.subscriptions.push(
        this.searchService.freeSearchSuggest(suggestPayload).subscribe(
          (result) => {
            this.freeSearchFilteredSuggestItems.set(result);
          },
          (error) => console.error(error),
        ),
      );
    }
    this.freeSearchPopularSuggestItems.set(null);
  }

  private updatePlaceSuggestions(term: string): void {
    if (this.tools.isNil(term) || term.length === 0 || term === this.SearchWidgetLabelsEnum.NARA_MIG) {
      return this.updatePlaceSuggestionsWithPopularPlaces();
    }
    return this.updatePlaceSuggestionsWithQuery(term);
  }

  private updatePlaceSuggestionsWithPopularPlaces(): void {
    if (!this.tools.isNil(this.places())) {
      this.placePopularSuggestItems.set(
        this.places()
          .popularPlaces.slice(0, SearchWidgetV4MobileSuggestionsComponent.NumberOfPopularToDisplay)
          .map(
            (item: SearchWidgetDataItem): SearchWidgetContentListItem => ({
              id: item.id,
              name: item.name,
              urlPath: item.urlPath,
              caregiverUnitsCount: item.caregiverUnitsCount,
              columnIndex: 0,
            }),
          ),
      );
      this.placeFilteredSuggestItems.set(null);
    }
  }

  private updatePlaceSuggestionsWithQuery(term: string): void {
    if (!this.tools.isNil(term) && term.length > 0) {
      const suggestPayload: SearchSuggestPayload = {
        term: encodeURIComponent(this.tools.clone(term).trim()),
        size: SearchWidgetV4MobileSuggestionsComponent.NumberOfSuggestionsToDisplay,
        types: [SearchSuggestTypesEnum.PLACE],
      };
      this.subscriptions.push(
        this.searchService.freeSearchSuggest(suggestPayload).subscribe(
          (result) => this.processSuggestData(result),
          (error) => console.error(error),
        ),
      );
      this.placePopularSuggestItems.set(null);
    }
  }

  private processSuggestData(suggestions: SearchSuggestItem[]): void {
    const suggestionsWithLabels: SearchSuggestItem[] = [];
    suggestions.forEach((item) => {
      item.suggestLabel = this.searchWidgetManagerService.getSuggestTypeLabel(item);
      suggestionsWithLabels.push(item);
    });
    this.placeFilteredSuggestItems.set(suggestionsWithLabels);
  }

  private closePanel(): void {
    this.searchService.isMobileWidgetExpanded.set(false);
  }

  private getFreeSearchSuggestItemBySuggestValueOrIndex(suggestValue: string, index?: number): SearchSuggestItem {
    let selectedItem: SearchSuggestItem = null;
    if (!this.tools.isNil(index) && !this.tools.isNil(this.freeSearchFilteredSuggestItems()[index])) {
      selectedItem = this.freeSearchFilteredSuggestItems()[index];
    } else {
      selectedItem = this.freeSearchFilteredSuggestItems().find((item) => item.suggestValue === suggestValue);
    }
    return selectedItem;
  }

  private updateSuggestions(): void {
    if (
      !this.tools.isNil(this.whichIsFocused()) &&
      !this.tools.isNil(this.state()) &&
      !this.tools.isNil(this.widgetData())
    ) {
      if (this.whichIsFocused() === SearchWidgetFreeSearchFieldsEnum.FREESEARCH) {
        this.isFreeSearchInInitialState.set(false);
        this.updateFreeSearchSuggestions(this.state().autocompleteCurrentValue);
      } else if (this.whichIsFocused() === SearchWidgetFreeSearchFieldsEnum.PLACE) {
        this.updatePlaceSuggestions(this.state().autocompletePlaceCurrentValue);
      }
    }
  }
}
